import type { OrgTreeNode } from '../api/keycloak-admin/org-config';
import { DEFAULT_MAP_VIEW, DEFAULT_ECHELON } from '../config/defaults';

/** Find a node by ID in the tree (recursive) */
export function findNodeById(nodes: OrgTreeNode[], targetId: string): OrgTreeNode | null {
  for (const node of nodes) {
    if (node.id === targetId) return node;
    const found = findNodeById(node.children, targetId);
    if (found) return found;
  }
  return null;
}

/** Update a node by ID, returning a new tree (immutable) */
export function updateNodeById(
  nodes: OrgTreeNode[],
  targetId: string,
  updates: Partial<OrgTreeNode>,
): OrgTreeNode[] {
  return nodes.map((node) => {
    if (node.id === targetId) {
      return { ...node, ...updates };
    }
    if (node.children.length > 0) {
      return { ...node, children: updateNodeById(node.children, targetId, updates) };
    }
    return node;
  });
}

/** Remove a node by ID, returning a new tree (immutable) */
export function removeNodeById(nodes: OrgTreeNode[], targetId: string): OrgTreeNode[] {
  return nodes
    .filter((node) => node.id !== targetId)
    .map((node) => ({
      ...node,
      children: removeNodeById(node.children, targetId),
    }));
}

/** Add a child node to a parent, returning a new tree (immutable) */
export function addChildToNode(
  nodes: OrgTreeNode[],
  parentId: string,
  child: OrgTreeNode,
): OrgTreeNode[] {
  return nodes.map((node) => {
    if (node.id === parentId) {
      return { ...node, children: [...node.children, child] };
    }
    if (node.children.length > 0) {
      return { ...node, children: addChildToNode(node.children, parentId, child) };
    }
    return node;
  });
}

/** Deep clone tree nodes with fresh UUIDs */
export function deepCloneWithNewIds(nodes: OrgTreeNode[]): OrgTreeNode[] {
  return nodes.map((node) => ({
    ...node,
    id: crypto.randomUUID(),
    children: deepCloneWithNewIds(node.children),
  }));
}

/** Create a new empty tree node with defaults */
export function createEmptyNode(name: string): OrgTreeNode {
  return {
    id: crypto.randomUUID(),
    name,
    shortName: '',
    type: '',
    echelon: DEFAULT_ECHELON,
    children: [],
    plugins: [],
    integrations: [],
    mapView: { ...DEFAULT_MAP_VIEW },
  };
}

/** Collect all nodes that have a specific plugin enabled */
export function collectNodesWithPlugin(
  nodes: OrgTreeNode[],
  pluginId: string,
): Array<{ name: string; shortName: string; echelon: string }> {
  const results: Array<{ name: string; shortName: string; echelon: string }> = [];

  for (const node of nodes) {
    if (node.plugins.includes(pluginId)) {
      results.push({ name: node.name, shortName: node.shortName, echelon: node.echelon });
    }
    results.push(...collectNodesWithPlugin(node.children, pluginId));
  }

  return results;
}
